/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { get } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { setAuthVar } from '../../utils/apollo-vars';
import { setToken } from '../../utils/auth';
import { sendSentryError } from '../../utils/sentry';
import { useTotpVerifyMutation } from '../../apollo';
import { getColor } from '../../styles';
import { useAlert, useModals } from '../../providers';
import { DesktopBr } from '../../styled';
import { TwoFACode } from '../TwoFACode';
import { CheckboxWithLabel as DefCheckbox } from '../form-elements';

const Login2FAModal = () => {
  const { t } = useTranslation();
  const { setAlert } = useAlert();
  const { data: modalData, dispatch } = useModals<'login2FA'>();

  const [backupCodeFieldVisible, setBackupCodeFieldVisible] = useState(false);
  const [rememberFlag, setRememberFlag] = useState(
    modalData?.rememberFlag ?? false
  );

  const title = useMemo(
    () =>
      !backupCodeFieldVisible ? (
        <>
          {t('LOGIN_MODAL__titleFirstLine')} <DesktopBr />
          {t('LOGIN_MODAL__titleSecondLine')}
        </>
      ) : (
        <>
          {t('LOGIN_MODAL__backupTitleFirstLine')} <DesktopBr />
          {t('LOGIN_MODAL__backupTitleSecondLine')}
        </>
      ),
    [backupCodeFieldVisible, t]
  );

  useEffect(() => {
    dispatch({
      type: 'setWrapperProps',
      payload: {
        modalName: 'login-2fa',
        titleText: title,
      },
    });
  }, [dispatch, title]);

  // TODO: should be added "remember" param
  const [verifyTOTP, { loading }] = useTotpVerifyMutation({
    fetchPolicy: 'no-cache',
    onCompleted: async ({ totpVerify }) => {
      const token = get(totpVerify, 'token');

      if (!token) {
        setAlert({ type: 'warning', message: t('LOGIN_MODAL__noTokenError') });
        sendSentryError(new Error(`Couldn't verify 2FA`));
        return;
      }

      setToken(token);
      setAuthVar(true);
      dispatch({ type: 'closeModal' });
    },
    onError: (error) => {
      setAlert({ type: 'error', message: t('LOGIN_MODAL__errorText') });
      sendSentryError(error);
    },
  });

  return (
    <TwoFACode
      loading={loading}
      submitCodeButtonText={t('TWO_FA_MODAL__loginButton')}
      childrenAfterInput={
        !!modalData?.socialProvider && (
          <Checkbox
            label={t('LOGIN_FORM__staySignedInPlaceholder')}
            name={'remember'}
            value={rememberFlag}
            onChange={(_, checked) => setRememberFlag(checked)}
          />
        )
      }
      onBackupCodeVisibilityChange={setBackupCodeFieldVisible}
      onCodeFill={(code) => verifyTOTP({ variables: { code } })}
    />
  );
};

// Same as in login form
const Checkbox = styled(DefCheckbox)`
  padding-top: 3px;
  /*noinspection CssUnusedSymbol*/
  .MuiFormControlLabel-label {
    color: ${getColor('boulder')};
  }
`;

export type Login2FAModalData = {
  socialProvider: boolean;
  rememberFlag: boolean;
};

export { Login2FAModal };
